/* eslint-disable jsx-a11y/anchor-is-valid */
/**
 * Entra External ID native-auth login page (single email+password form).
 *
 * Text comes from the app-wide react-intl catalogue (AUTH.NATIVE.* keys); the
 * language switcher goes through Metronic's setLanguage()/useLang(), the same
 * i18n system the rest of the app uses.
 *
 * The azure-template stylesheets are injected on mount and removed on unmount;
 * the new look is driven by the scoped native-* classes in native-auth.css.
 */
import {FormEvent, useEffect, useState} from 'react';
import {useIntl} from 'react-intl';

import type {AuthTokens} from './nativeAuthApi';
import AuthLayout from './AuthLayout';
import OtpScreen from './OtpScreen';
import PasswordField from './PasswordField';
import ResetPassword from './ResetPassword';
import {useSignIn} from './useSignIn';
import {LANGUAGES, getLanguageMeta} from './nativeLanguages';
import {ArrowRightIcon, ChevronDownIcon, EnvelopeIcon, LockIcon} from './nativeIcons';
import {toAbsoluteUrl} from '../../../../_metronic/helpers';
import {useLang, setLanguage} from '../../../../_metronic/i18n/Metronici18n';
import './native-auth.css';

const STYLESHEETS = [
  'https://fonts.googleapis.com/css?family=Montserrat:300,400,500,600,700,800',
  'https://login.celeste.fr/azure-template/v2/main.css',
  'https://login.celeste.fr/azure-template/v2/azureform.css',
];

const LOGO_DARK = toAbsoluteUrl('/media/logos/LOGO_CELESTE_Sans_Baseline_Bleu.png');
const LOGO_WHITE = toAbsoluteUrl('/media/logos/LOGO_CELESTE_Sans_Baseline_Blanc.png');
const STAR_BG = 'https://login.celeste.fr/azure-template/star.svg';
const CREATE_ACCOUNT_URL = 'https://espace-clients.celeste.fr/public/create-account';
const OLD_SITE_CELESTE_URL = 'https://espace-clients-v1.celeste.fr/login';
const OLD_SITE_STELLA_URL = 'https://login.celeste.fr/';
const LEGAL_URL = 'https://www.celeste.fr/mentions-legales/';

interface NativeLoginProps {
  /** Called once sign-in completes. */
  onCompleted?: (tokens: AuthTokens) => void;
}

const NativeLogin = ({onCompleted}: NativeLoginProps): JSX.Element => {
  const intl = useIntl();
  const f = (id: string): string => intl.formatMessage({id});
  const lang = useLang();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [langOpen, setLangOpen] = useState(false);
  const [showRetrieveId, setShowRetrieveId] = useState(false);
  const [showOldSite, setShowOldSite] = useState(false);
  const [showReset, setShowReset] = useState(false);

  const {status, error, tokens, mfa, resetReason, signIn, submitMfaCode, resendMfaCode, reset} =
    useSignIn();

  // Scope the B2C template styles to this screen only.
  useEffect(() => {
    const links = STYLESHEETS.map((href) => {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = href;
      link.dataset.nativeLogin = 'true';
      document.head.appendChild(link);
      return link;
    });
    return () => links.forEach((link) => link.remove());
  }, []);

  useEffect(() => {
    if (status === 'completed' && tokens) {
      onCompleted?.(tokens);
    }
  }, [status, tokens, onCompleted]);

  const submitting = status === 'submitting';
  const requestAccess = f('AUTH.NATIVE.LOGIN.REQUEST_ACCESS');

  const handleSubmit = (event: FormEvent): void => {
    event.preventDefault();
    signIn(email.trim(), password);
  };

  // Reset flow: opened by the "forgot password" link, or forced by the backend
  // when a reset is required before sign-in — either a JIT-migration weak
  // password (AADSTS50219) or an expired / force-change password (AADSTS50055).
  const forcedReset = status === 'passwordResetRequired';
  const forcedNoticeKey =
    resetReason === 'expired'
      ? 'AUTH.NATIVE.RESET.EXPIRED_NOTICE'
      : 'AUTH.NATIVE.RESET.REQUIRED_NOTICE';
  if (showReset || forcedReset) {
    return (
      <ResetPassword
        initialEmail={forcedReset ? email.trim() : undefined}
        notice={forcedReset ? f(forcedNoticeKey) : undefined}
        title={forcedReset ? f('AUTH.NATIVE.RESET.REQUIRED_TITLE') : undefined}
        onClose={() => {
          setShowReset(false);
          if (forcedReset) {
            reset();
          }
        }}
      />
    );
  }

  if (status === 'mfaRequired' || status === 'verifyingMfa') {
    return (
      <AuthLayout>
        <OtpScreen
          title={f('AUTH.NATIVE.VERIFICATION')}
          sentTo={mfa?.sentTo}
          verifying={status === 'verifyingMfa'}
          error={error}
          onSubmit={submitMfaCode}
          onResend={resendMfaCode}
        />
      </AuthLayout>
    );
  }

  return (
    <div id="root" className="native-auth">
      <div className="native-shell">
        {/* Left panel */}
        <div className="native-left">
          <div className="native-topbar">
            <img className="native-logo" src={LOGO_DARK} alt="CELESTE" />

            <div className="native-lang">
              <button
                type="button"
                className="native-lang-btn"
                aria-haspopup="listbox"
                aria-expanded={langOpen}
                onClick={() => setLangOpen((open) => !open)}
              >
                <img className="native-lang-flag" src={getLanguageMeta(lang).flag} alt="" />
                <span>{lang.toUpperCase()}</span>
                <ChevronDownIcon />
              </button>
              {langOpen ? (
                <>
                  <div className="native-lang-overlay" onClick={() => setLangOpen(false)} />
                  <ul className="native-lang-menu" role="listbox">
                    {LANGUAGES.map((l) => (
                      <li key={l.lang}>
                        <button
                          type="button"
                          className={l.lang === lang ? 'is-active' : undefined}
                          role="option"
                          aria-selected={l.lang === lang}
                          onClick={() => setLanguage(l.lang)}
                        >
                          <img className="native-lang-flag" src={l.flag} alt="" />
                          <span>{l.name}</span>
                        </button>
                      </li>
                    ))}
                  </ul>
                </>
              ) : null}
            </div>
          </div>

          <div className="native-form-col">
            <form
              className="native-form"
              onSubmit={handleSubmit}
              aria-label={f('AUTH.NATIVE.LOGIN.TITLE')}
            >
              <h2 className="native-title">{f('AUTH.NATIVE.LOGIN.TITLE')}</h2>
              <p className="native-subtitle">{f('AUTH.NATIVE.LOGIN.SUBTITLE')}</p>

              {error ? (
                <div className="error pageLevel" role="alert">
                  <p className="mb-0">{error}</p>
                </div>
              ) : null}

              {/* Email */}
              <div className="native-field">
                <label className="native-label" htmlFor="email">
                  {f('AUTH.NATIVE.FIELD.EMAIL')}
                </label>
                <div className={error ? 'native-input input-error' : 'native-input'}>
                  <span className="field-left-icon">
                    <EnvelopeIcon />
                  </span>
                  <input
                    type="email"
                    id="email"
                    placeholder={f('AUTH.NATIVE.FIELD.EMAIL')}
                    aria-label={f('AUTH.NATIVE.FIELD.EMAIL')}
                    autoComplete="username"
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                    autoFocus
                    required
                  />
                </div>
              </div>

              {/* Password */}
              <div className="native-field">
                <label className="native-label" htmlFor="password">
                  {f('AUTH.NATIVE.FIELD.PASSWORD')}
                </label>
                <PasswordField
                  id="password"
                  leftIcon={<LockIcon />}
                  placeholder={f('AUTH.NATIVE.FIELD.PASSWORD')}
                  aria-label={f('AUTH.NATIVE.FIELD.PASSWORD')}
                  autoComplete="current-password"
                  aria-required="true"
                  className={error ? 'input-error' : undefined}
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  required
                />
              </div>

              {/* Forgot password */}
              <div className="native-row native-row--end">
                <a
                  href="#"
                  className="native-link"
                  onClick={(event) => {
                    event.preventDefault();
                    setShowReset(true);
                  }}
                >
                  {f('AUTH.NATIVE.LOGIN.FORGOT')}
                </a>
              </div>

              <button type="submit" className="native-submit" disabled={submitting}>
                <span>
                  {submitting ? f('AUTH.NATIVE.LOGIN.SUBMITTING') : f('AUTH.NATIVE.LOGIN.SUBMIT')}
                </span>
                {submitting ? null : <ArrowRightIcon />}
              </button>

              <div className="native-cta">
                <span className="native-cta-text">{f('AUTH.NATIVE.LOGIN.NO_ID')}</span>
                <a
                  href="#"
                  className="native-link native-cta-link"
                  onClick={(event) => {
                    event.preventDefault();
                    setShowOldSite(false);
                    setShowRetrieveId(true);
                  }}
                >
                  {requestAccess.charAt(0)}
                  <span className="native-underline-mid">{requestAccess.slice(1, -1)}</span>
                  {requestAccess.slice(-1)}
                </a>
              </div>
            </form>

            {/* Retrieve identifier */}
            {showRetrieveId ? (
              <div id="retrieve-id-section">
                <hr className="native-divider" />
                <h2 className="native-block-title">{f('AUTH.NATIVE.RETRIEVE.TITLE')}</h2>
                <div className="native-retrieve">
                  <div className="native-retrieve-card native-retrieve-card--muted">
                    <h5 className="native-retrieve-heading">
                      {f('AUTH.NATIVE.RETRIEVE.COMPANY_TITLE')}
                    </h5>
                    <p className="native-retrieve-body">{f('AUTH.NATIVE.RETRIEVE.COMPANY_BODY')}</p>
                  </div>
                  <div className="native-retrieve-card">
                    <h5 className="native-retrieve-heading">
                      {f('AUTH.NATIVE.RETRIEVE.ADMIN_TITLE')}
                    </h5>
                    <a
                      href={CREATE_ACCOUNT_URL}
                      target="_blank"
                      rel="noreferrer"
                      className="native-secondary-btn"
                    >
                      {f('AUTH.NATIVE.RETRIEVE.ADMIN_BUTTON')}
                    </a>
                  </div>
                </div>
              </div>
            ) : null}

            {/* Old client spaces */}
            {showOldSite ? (
              <div className="native-oldsite">
                <h6 className="native-block-title">{f('AUTH.NATIVE.OLDSITE.TITLE')}</h6>
                <div className="native-oldsite-buttons">
                  <a
                    href={OLD_SITE_CELESTE_URL}
                    target="_blank"
                    rel="noreferrer"
                    className="native-secondary-btn"
                  >
                    CELESTE
                  </a>
                  <a
                    href={OLD_SITE_STELLA_URL}
                    target="_blank"
                    rel="noreferrer"
                    className="native-secondary-btn native-secondary-btn--alt"
                  >
                    Stella Telecom
                  </a>
                </div>
                <p className="native-oldsite-note">
                  <span className="native-oldsite-warn">
                    {f('AUTH.NATIVE.OLDSITE.WARNING_LABEL')} :
                  </span>{' '}
                  {f('AUTH.NATIVE.OLDSITE.WARNING_BODY')}
                </p>
              </div>
            ) : null}

            <div className="native-footer">
              <a
                href="#"
                className="native-footer-link"
                onClick={(event) => {
                  event.preventDefault();
                  setShowRetrieveId(false);
                  setShowOldSite(true);
                }}
              >
                {f('AUTH.NATIVE.LOGIN.OLD_SPACES')}
              </a>
              <span className="native-footer-sep">·</span>
              <a href={LEGAL_URL} target="_blank" rel="noreferrer" className="native-footer-link">
                {f('AUTH.NATIVE.LEGAL')}
              </a>
            </div>
          </div>
        </div>

        {/* Right panel — lighter navy with two faint star watermarks */}
        <div className="native-right">
          <img className="native-right-star" src={STAR_BG} alt="" aria-hidden="true" />
          <img
            className="native-right-star native-right-star-2"
            src={STAR_BG}
            alt=""
            aria-hidden="true"
          />
          <div className="native-right-inner">
            <img className="native-right-logo" src={LOGO_WHITE} alt="Espace client Celeste" />
            <h1 className="native-right-title">{f('AUTH.NATIVE.ESPACE_CLIENT')}</h1>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NativeLogin;
